import { Loader } from "lucide-react";
import { Controller } from "react-hook-form";
import { Button } from "../../../../../shared/components/ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../shared/components/ui/dialog.js";
import { Label } from "../../../../../shared/components/ui/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../shared/components/ui/select.js";
import { Textarea } from "../../../../../shared/components/ui/textarea.js";
import {
  RECIPIENT_FIELD_STATUS,
  SALE_REOPEN_REQUEST_MODAL,
} from "../config/sale-reopen-request-modal.config.js";
import { resolveRecipientFieldStatus } from "../lib/resolve-recipient-field-status.js";

/**
 * @param {Object} props
 * @param {string} props.message
 * @param {() => void} props.onRetry
 * @param {boolean} props.disabled
 */
function RecipientErrorState({ message, onRetry, disabled }) {
  const copy = SALE_REOPEN_REQUEST_MODAL.recipient;
  return (
    <div className="space-y-2">
      <p className="text-xs text-red-500">{message || copy.errorFallback}</p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRetry}
        disabled={disabled}
      >
        {copy.retryLabel}
      </Button>
    </div>
  );
}

function RecipientLoadingState() {
  const copy = SALE_REOPEN_REQUEST_MODAL.recipient;
  return (
    <div className="flex h-10 items-center gap-2 rounded-md border px-3 text-sm text-gray-600">
      <Loader className="h-4 w-4 animate-spin" />
      {copy.loadingLabel}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {import("react-hook-form").Control} props.control
 * @param {{ value: string, label: string }[]} props.options
 * @param {boolean} props.disabled
 */
function RecipientSelectField({ control, options, disabled }) {
  const copy = SALE_REOPEN_REQUEST_MODAL.recipient;
  return (
    <Controller
      name="recipientToken"
      control={control}
      render={({ field }) => (
        <Select
          value={field.value || undefined}
          onValueChange={field.onChange}
          disabled={disabled || options.length === 0}
        >
          <SelectTrigger id={copy.id} className="h-10 w-full">
            <SelectValue placeholder={copy.placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );
}

const RECIPIENT_FIELD_REGISTRY = {
  [RECIPIENT_FIELD_STATUS.loading]: RecipientLoadingState,
  [RECIPIENT_FIELD_STATUS.error]: RecipientErrorState,
  [RECIPIENT_FIELD_STATUS.ready]: RecipientSelectField,
};

/**
 * Modal presentacional de solicitação de reabertura.
 * Estilo alinhado ao `AuditObservationModal` (cores, botões, tipografia).
 * Sem fetch e sem toast — só props → JSX.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {import("react-hook-form").UseFormReturn} props.form
 * @param {{ value: string, label: string }[]} props.options
 * @param {boolean} props.optionsLoading
 * @param {string|null} props.optionsError
 * @param {() => void} props.onReloadOptions
 * @param {(e?: React.BaseSyntheticEvent) => Promise<void>} props.onSubmit
 * @param {boolean} props.isSubmitting
 * @param {boolean} props.canSubmit
 */
export function SaleReopenRequestModal({
  open,
  onClose,
  form,
  options,
  optionsLoading,
  optionsError,
  onReloadOptions,
  onSubmit,
  isSubmitting,
  canSubmit,
}) {
  const copy = SALE_REOPEN_REQUEST_MODAL;
  const {
    control,
    register,
    formState: { errors },
  } = form;

  const recipientStatus = resolveRecipientFieldStatus({
    optionsLoading,
    optionsError,
  });
  const RecipientField = RECIPIENT_FIELD_REGISTRY[recipientStatus];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose?.();
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label
              htmlFor={copy.recipient.id}
              className="text-sm font-medium text-gray-600"
            >
              {copy.recipient.label}
            </Label>
            <RecipientField
              control={control}
              options={options}
              disabled={isSubmitting}
              message={optionsError}
              onRetry={onReloadOptions}
            />
            {errors.recipientToken?.message ? (
              <p className="text-xs text-red-500">
                {errors.recipientToken.message}
              </p>
            ) : null}
          </div>

          <div className="grid gap-2">
            <Label
              htmlFor={copy.reason.id}
              className="text-sm font-medium text-gray-600"
            >
              {copy.reason.label}
            </Label>
            <Textarea
              id={copy.reason.id}
              placeholder={copy.reason.placeholder}
              disabled={isSubmitting}
              rows={5}
              className="resize-y min-h-[120px]"
              {...register("requesterReason")}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              className="btn-cancel"
              variant="destructive"
              onClick={() => onClose?.()}
              disabled={isSubmitting}
            >
              {copy.cancel}
            </Button>
            <Button
              type="submit"
              className="btn-primary"
              variant="default"
              disabled={!canSubmit}
            >
              {isSubmitting && (
                <Loader className="h-4 w-4 animate-spin mr-2" />
              )}
              {isSubmitting ? copy.confirmLoading : copy.confirm}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
